import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'

import { answerClauses, parseAnswer } from '../../lib/answer-markup'
import { citationsPop, streamClauses } from '../../lib/motion'
import {
  useChatAttachmentUrl,
  type ChatAttachment,
  type ChatMessage,
  type ChatScopeId,
  type ChatSource,
} from '../../lib/data'
import { SparkleSmall } from '../interests/icons'
import { AnswerBody } from './AnswerBody'
import { AlertCircle, PlayCircle } from './icons'
import { ProposalCard } from './ProposalCard'

// ============================================================================
// ONE TURN OF THE CONVERSATION — LifeOS Chat.dc.html, option 11a.
//
// README §10: "User bubbles right-aligned, max 620px, `ink`, white 400
// 13.5px, radius `14px 14px 4px 14px`. Assistant answers left-aligned behind a
// 26px violet avatar tile, 400 13.5px / 1.65 line-height, max 760px,
// `text-wrap:pretty`."
//
// SOURCE CARDS ARE NOT DECORATION AND THEY ARE NOT OPTIONAL. README §10:
// "**Every factual claim about the user's life carries these.**" §Interactions:
// "If a claim has no source, that is a bug — the design shows it caught, not
// shipped." So this component renders that catch: an assistant message that
// arrives with no sources does NOT quietly render as prose. It renders the answer
// with a red strip naming the failure, because silently dropping the citation row
// would turn a caught bug into a shipped one.
//
// Every card LINKS. The locator strings are derived from the entity by the data
// contract, and the href is that entity's real route — a transcript card carries
// the matched phrase in `?q=`, which is how the lecture page lands you on the
// segment instead of at the top of an 87-minute recording.
// ============================================================================

export function Message({
  message,
  onWiden,
  threadId,
  stream = false,
}: {
  message: ChatMessage
  onWiden: (scopeId: ChatScopeId) => void
  /** Which thread a proposal decision should re-read. Absent on a preview with no thread. */
  threadId?: string
  /**
   * 13e. True only for an answer that arrived while you were watching. A thread you scrolled back
   * to does not re-stream its history — streaming is what an answer being written looks like, and
   * replaying it on old messages would be theatre.
   */
  stream?: boolean
}) {
  if (message.role === 'user') {
    return (
      <div className="flex flex-none flex-col items-end gap-[7px]">
        {/* THE PICTURE SITS ABOVE THE QUESTION, in the same column and the same max width. It is
            what the answer was looking at, so it belongs in the conversation rather than in a
            collapsed "1 attachment" affordance the owner has to open to see what he sent. */}
        {message.attachments.length > 0 && (
          <div className="flex max-w-[620px] flex-wrap justify-end gap-[7px]">
            {message.attachments.map((attachment) => (
              <SentPicture key={attachment.id} attachment={attachment} />
            ))}
          </div>
        )}
        <p className="max-w-[620px] rounded-[14px_14px_4px_14px] bg-ink px-4 py-3 text-[13.5px] font-normal leading-[1.5] text-surface">
          {message.text}
        </p>
      </div>
    )
  }

  return <AssistantMessage message={message} onWiden={onWiden} threadId={threadId} stream={stream} />
}

/**
 * 13e · THE ANSWER ARRIVES CLAUSE BY CLAUSE, AND ONLY THEN CITES ITSELF.
 *
 * "A chip must never appear mid-claim — that implies the model is citing something it hasn't said
 * yet. Citations land after the sentence they support." That is a truth claim about the order of
 * two things on screen, not a taste call about timing, which is why `citing` is gated on the LAST
 * clause finishing rather than on a fixed delay that a long answer would outrun.
 *
 * The whole reveal is inside a `prefers-reduced-motion` guard by way of the sequencer: under
 * reduced motion every clause commits at once and the sources are there on the same tick. The
 * ORDER survives; only the waiting goes.
 */
function AssistantMessage({
  message,
  onWiden,
  threadId,
  stream,
}: {
  message: ChatMessage
  onWiden: (scopeId: ChatScopeId) => void
  threadId: string | undefined
  stream: boolean
}) {
  // The clause count drives the reveal; the elements themselves come from AnswerBody, which
  // marks each clause it renders. Recomputed only when the text does.
  const clauses = useMemo(
    () => (stream ? answerClauses(parseAnswer(message.text)) : null),
    [stream, message.text],
  )
  const prose = useRef<HTMLDivElement>(null)
  const chips = useRef<HTMLDivElement>(null)
  // Sources are absent from the DOM until the last clause has landed — not hidden, absent. A chip
  // that is merely transparent is still in the accessibility tree, which would let a screen reader
  // read the citation before the claim.
  const [citing, setCiting] = useState(!stream)
  // THE REVEAL ENDS IN PLAIN TEXT. Each clause is drawn transparent and only an animation's fill
  // holds it visible, so anything that drops that fill leaves a hole in the answer — on
  // 2026-10-09 three of four bullets stayed blank under sources that HAD appeared, which only
  // happens after the run finished. Once it has, the answer is re-drawn without the clause
  // wrappers at all, so nothing visible depends on an animation still being alive.
  const [revealed, setRevealed] = useState(false)

  useEffect(() => {
    if (!clauses) return
    const nodes = prose.current?.querySelectorAll<HTMLElement>('[data-clause]')
    const run = streamClauses(nodes ? [...nodes] : [])
    let abandoned = false
    void run.finished.then(() => {
      if (abandoned) return
      setRevealed(true)
      setCiting(true)
    })
    return () => {
      abandoned = true
      run.cancel()
    }
  }, [clauses])

  useEffect(() => {
    if (!citing || !stream) return
    const run = citationsPop(chips.current, 0)
    return () => run.cancel()
  }, [citing, stream])

  return (
    <div className="flex flex-none gap-[13px]">
      <span
        className="flex size-[26px] flex-none items-center justify-center rounded-tile-sm border border-accent-line bg-accent-fill text-chat"
        aria-hidden
      >
        <SparkleSmall size={14} strokeWidth={1.8} />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-[11px]">
        {/* Paragraphs, bullets and bold, as the model shaped them (owner, 2026-09-20). The 760px
            and the type are the handoff's; the shape inside is new. */}
        <div
          ref={prose}
          className="max-w-[760px] text-[13.5px] font-normal leading-[1.65] text-ink text-pretty"
        >
          <AnswerBody text={message.text} stream={clauses !== null && !revealed} />
        </div>

        {citing &&
          (message.sources.length > 0 ? (
            <>
              <div className="flex items-center gap-2">
                <span className="text-[9.5px] font-semibold leading-none tracking-[0.08em] text-microcopy">
                  SOURCES
                </span>
              </div>
              <div ref={chips} className="flex flex-wrap gap-[9px]">
                {message.sources.map((source, i) => (
                  <SourceCard key={`${source.href}-${i}`} source={source} />
                ))}
              </div>
            </>
          ) : (
            <UncitedClaim />
          ))}

        {/* THE CARDS COME AFTER THE CITATIONS, and only once `citing` is true — the same rule
            13e states for the source chips, for the same reason: a card offering to put something
            on the calendar before the sentence that justifies it has finished arriving is the
            screen asking for a decision the reader has not been given the grounds for. */}
        {citing && message.proposals.length > 0 && (
          <div className="flex flex-col gap-[9px]">
            {message.proposals.map((proposal) => (
              <ProposalCard key={proposal.id} proposal={proposal} threadId={threadId} />
            ))}
          </div>
        )}

        {message.scopeLimit && (
          <div className="flex max-w-[760px] items-center gap-[9px] rounded-row border border-warning-line bg-warning-fill px-[13px] py-[9px]">
            <AlertCircle className="flex-none text-warning-2" />
            <p className="flex-1 text-[11.5px] font-normal leading-[1.45] text-warning text-pretty">
              {message.scopeLimit.text}
            </p>
            <button
              type="button"
              onClick={() => onWiden(message.scopeLimit!.widenTo)}
              className="flex-none rounded-chip border border-warning-line bg-surface px-[10px] py-[4px] text-[10.5px] font-semibold leading-[1.4] text-warning-2 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-warning-fill"
            >
              {message.scopeLimit.widenLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * ONE PICTURE THE OWNER SENT.
 *
 * The bytes are behind his token, so an `<img src>` pointing at the API would 401 — a browser
 * does not put an Authorization header on an image request. The data layer fetches it and hands
 * back an object URL (remote.ts → `getChatAttachmentUrl`).
 *
 * A LOAD FAILURE SAYS SO IN WORDS. A broken-image glyph over a dark bubble reads as a rendering
 * bug; "that picture could not be loaded" reads as what it is, and the question it was sent with
 * is still right there and still answerable.
 */
function SentPicture({ attachment }: { attachment: ChatAttachment }) {
  const { data: url, error, isPending } = useChatAttachmentUrl(attachment.id)
  if (isPending) {
    return <span className="size-[96px] animate-pulse rounded-tile-sm bg-surface-4" aria-hidden />
  }
  if (error || !url) {
    return (
      <span className="flex max-w-[240px] items-center rounded-tile-sm border border-dashed border-dash px-[11px] py-[9px] text-[10.5px] leading-[1.4] text-microcopy">
        That picture could not be loaded. Your question is still below it.
      </span>
    )
  }
  return (
    <img
      src={url}
      alt="A picture you sent with this question"
      className="max-h-[220px] max-w-[280px] rounded-tile-sm border border-line object-cover"
    />
  )
}

/**
 * THE CAUGHT BUG. An answer about the owner's life with nothing behind it is not rendered as
 * though it were fine. The handoff shows this state on purpose, and the copy follows the pattern:
 * name the consequence, name what still works, offer exactly one action — and there is no action,
 * because there is nothing the reader can press that would make a citation appear.
 */
function UncitedClaim() {
  return (
    <div
      role="status"
      className="flex max-w-[760px] items-start gap-[9px] rounded-row border border-danger-line bg-danger-fill px-[13px] py-[9px]"
    >
      <span className="mt-[5px] size-[5px] flex-none rounded-full bg-danger-dot" aria-hidden />
      <p className="text-[11.5px] leading-[1.45] text-danger text-pretty">
        This answer came back with no sources, so nothing above it can be checked. Treat it as
        unverified — every other answer on this screen links to what it read.
      </p>
    </div>
  )
}

/** The 236px card. Radius 11, 1px line, a type pill, a derived locator, a quiet third line. */
function SourceCard({ source }: { source: ChatSource }) {
  return (
    <Link
      to={source.href}
      className={clsx(
        'flex w-[236px] flex-col gap-[7px] rounded-row border border-line bg-surface px-3 py-[10px]',
        'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-surface-2',
      )}
    >
      <span className="flex items-center gap-[7px]">
        <span className={clsx('rounded-pill px-[6px] py-[2px] text-[9px] font-semibold leading-[1.6] tracking-[0.04em]', PILL[source.kind])}>
          {source.pill}
        </span>
      </span>

      <span className="text-[11.5px] font-medium leading-[1.35] text-ink-2">{source.locator}</span>

      {source.timestampMs !== null ? (
        <span className="flex items-center gap-[6px] text-[10px] font-semibold leading-none text-success-dot">
          <PlayCircle />
          Jump to timestamp
        </span>
      ) : (
        source.detail && (
          <span className="text-[10px] font-normal leading-none text-microcopy">
            {source.detail}
          </span>
        )
      )}
    </Link>
  )
}

/**
 * Type-pill grounds, read off the mockup: school things take the info blue, journal things the
 * journal amber, and a voice memo the paler `info-fill-2` / `info-2` pair — which is the design
 * saying a memo is a recording, not a page.
 */
const PILL: Record<ChatSource['kind'], string> = {
  transcript: 'bg-info-fill text-info',
  announcement: 'bg-info-fill text-info',
  syllabus: 'bg-warning-fill text-warning-2',
  journal: 'bg-warning-fill text-warning-2',
  voice_memo: 'bg-info-fill-2 text-info-2',
  // V4's three: the handoff drew five kinds, all School or Journal, and never drew a citation for
  // a task or a learning item — because it never drew an answer at those scopes. Each takes its
  // own domain's hue from the token file, the way every other pill on this screen does: an
  // assignment is School blue, a task is the Tasks orange, a learning item the Learning teal.
  assignment: 'bg-info-fill text-info',
  task: 'bg-orange-fill text-orange',
  learning_item: 'bg-teal-fill text-teal',
  // School BLUE, deliberately not the amber `syllabus` takes one line above it. The amber on this
  // screen is the same amber the provenance chips wear: it means a model made this. A course file
  // is the professor's own PDF, transcribed by a text extractor with no model anywhere near it —
  // so it takes the hue of the synced School data it is, and colour keeps saying the one thing it
  // is used to say here.
  course_file: 'bg-info-fill text-info',
}

/**
 * THE QUESTION, ON ITS WAY — owner, 2026-09-23: "instead of having my question sit in the text
 * entry box while this happens, it gets sent into chat and then we wait for the LLM to respond."
 *
 * The question is drawn as the sent bubble it is about to become, and under it the wait is stated
 * in words beside the answer's avatar. Nothing here is an answer and nothing pretends to stream —
 * the engine returns a whole answer at once. If the send fails, this goes away and the question
 * goes back into the field (Composer), which is where the refusal says it is.
 */
export function PendingTurn({ text, mode }: { text: string; mode: 'quick' | 'deep' }) {
  return (
    <>
      <div className="flex flex-none flex-col items-end gap-[7px]">
        <p className="max-w-[620px] whitespace-pre-wrap rounded-[14px_14px_4px_14px] bg-ink px-4 py-3 text-[13.5px] font-normal leading-[1.5] text-surface">
          {text}
        </p>
      </div>
      <div className="flex flex-none items-center gap-[13px]" role="status">
        <span
          className="flex size-[26px] flex-none animate-pulse items-center justify-center rounded-tile-sm border border-accent-line bg-accent-fill text-chat"
          aria-hidden
        >
          <SparkleSmall size={14} strokeWidth={1.8} />
        </span>
        <span className="text-[12.5px] leading-[1.5] text-microcopy">
          Reading your {mode === 'deep' ? 'transcripts and entries in full' : 'entries and summaries'}…
        </span>
      </div>
    </>
  )
}
